/*!

=========================================================
* Light Bootstrap Dashboard React - v2.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/light-bootstrap-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useEffect } from "react";
import { useLocation, NavLink, useHistory } from "react-router-dom";
import { X, LogOut } from "lucide-react";

import { Nav } from "react-bootstrap";

import logo from "assets/img/reactlogo.png";

function Sidebar({ color, image, routes }) {
  const location = useLocation();
  const history = useHistory();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const activeRoute = (routeName) => {
    return location.pathname.indexOf(routeName) > -1 ? "active" : "";
  };

  // Observer l'ouverture du sidebar
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isOpen = document.documentElement.classList.contains('nav-open');
          if (isOpen) {
            // Délai pour l'animation d'entrée
            setTimeout(() => setIsVisible(true), 150);
          } else {
            setIsVisible(false);
          }
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!showLogoutConfirm) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showLogoutConfirm]);

  const handleCloseSidebar = () => {
    setIsClosing(true);
    
    // Animation de sortie
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      
      if (window.closeSidebarFromBurger) {
        window.closeSidebarFromBurger();
      }
    }, 300);
  };

  const handleLogoutClick = (e) => {
    e.preventDefault();
    setShowLogoutConfirm(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("role");
    localStorage.removeItem("userIdStorage");
    history.push("/login");
  };

  return (
    <>
      <div className="sidebar" data-image={image} data-color={color}>
        {/* Bouton burger animé dans le sidebar */}
        <button
          className={`sidebar-burger-btn ${isVisible ? 'burger-fly-in' : ''} ${isClosing ? 'burger-fly-out-reverse' : ''}`}
          onClick={handleCloseSidebar}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#CE9136',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1050,
            opacity: isVisible && !isClosing ? 1 : 0,
            transform: isVisible && !isClosing ? 'scale(1) translateX(0)' : 'scale(0.5) translateX(-100px)',
            transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
        >
          <X size={20} color="#fff" />
        </button>

        <div className="sidebar-wrapper">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <img
              src={require("../../assets/img/logo_lts.png")}
              alt="Logo"
              className="logo"
            />
          </div>
          <Nav>
            {routes.map((prop, key) => {
              if (!prop.redirect && prop.display)
                return (
                  <li
                    className={
                      prop.upgrade
                        ? "active active-pro"
                        : activeRoute(prop.layout + prop.path)
                    }
                    key={key}>
                    <NavLink
                      to={prop.layout + prop.path}
                      className="nav-link"
                      activeClassName="active">
                      {/*  <i className={prop.icon} /> */}
                      <img
                        src={prop.icon}
                        alt={prop.name}
                        className="nav-icon"
                        style={{ height: "26px", marginRight: "10px" }}
                      />
                      <p>{prop.name}</p>
                    </NavLink>
                  </li>
                );
              return null;
            })}
            <li>
              <a
                href="#logout"
                className="nav-link"
                onClick={handleLogoutClick}
                style={{
                  backgroundColor: "#dc3545",
                  opacity: 1,
                  display: "flex",
                  alignItems: "center",
                }}>
                <LogOut
                  size={26}
                  color="#fff"
                  style={{ marginRight: "10px" }}
                />
                <p>Se déconnecter</p>
              </a>
            </li>
          </Nav>
        </div>
      </div>

      {showLogoutConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 3000,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            overflow: "hidden",
          }}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-confirm-title"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(420px, calc(100vw - 40px))",
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
              padding: "24px",
            }}>
            <button
              type="button"
              aria-label="Fermer"
              onClick={() => setShowLogoutConfirm(false)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                border: "none",
                background: "transparent",
                color: "#777",
                cursor: "pointer",
                padding: 4,
              }}>
              <X size={20} />
            </button>
            <h3
              id="logout-confirm-title"
              style={{
                margin: "0 32px 12px 0",
                color: "#242731",
                fontSize: "22px",
                fontWeight: 700,
              }}>
              Confirmer la déconnexion
            </h3>
            <p
              style={{
                margin: "0 0 24px",
                color: "#555",
                fontSize: "16px",
                lineHeight: 1.5,
              }}>
              Voulez-vous vraiment vous déconnecter ?
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}>
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  border: "1px solid #cfd3d8",
                  backgroundColor: "#fff",
                  color: "#555",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: 600,
                  padding: "10px 16px",
                }}>
                Annuler
              </button>
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  border: "1px solid #dc3545",
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: 600,
                  padding: "10px 16px",
                }}>
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
